import type { CardsTranslations } from "@/shared/lang/interfaces/cards";

import type { CardCopTopUp } from "../types/cardCopTopUp.types";
import type {
  CardCopTopUpTiming,
  CardFundingOptions,
  CardFundingSource,
  CardFundingSourceKind,
} from "../types/cardFundingOptions.types";
import { copTopUpUnavailableKey } from "./cardCopTopUp";

/** The COP option is not offered at all while pesos are not rolled out for the organization. */
export const COP_NOT_ROLLED_OUT = "card_cop_top_ups_not_enabled";

/**
 * The sources the load dialog lists, recommended first. COP while not rolled out is dropped (an organization
 * without the rollout never sees it); a card wallet the backend did not list does not exist for this card.
 */
export function listedFundingSources(options: CardFundingOptions | null): CardFundingSource[] {
  if (!options) return [];
  return options.sources.filter(
    (source) => !(source.kind === "COP" && source.unavailable_reason === COP_NOT_ROLLED_OUT),
  );
}

/** Whether any listed source can be used right now; false renders the "ask support" state. */
export function hasUsableFundingSource(options: CardFundingOptions | null): boolean {
  return listedFundingSources(options).some((source) => source.available);
}

/** The first usable source, so the dialog opens on the recommended one. */
export function defaultFundingSource(options: CardFundingOptions | null): CardFundingSourceKind | null {
  return listedFundingSources(options).find((source) => source.available)?.kind ?? null;
}

/** Why a source is off, as an i18n key of the `cards` namespace. */
export function fundingUnavailableKey(source: CardFundingSource): keyof CardsTranslations {
  if (source.kind === "COP") return copTopUpUnavailableKey(source.unavailable_reason);
  switch (source.unavailable_reason) {
    case "card_deposits_not_enabled":
      return "card_load_unavailable_crypto_off";
    case "card_not_active":
      return "card_load_unavailable_card_inactive";
    case "card_not_eligible":
      return "card_load_unavailable_card";
    default:
      return "card_load_unavailable_generic";
  }
}

/** Whole minutes for a duration, never 0 for something that takes time. */
export function minutesFor(seconds: number): number {
  return Math.max(1, Math.round(seconds / 60));
}

export type LoadTimelineStageId = "debited" | "sending" | "arrived" | "credited";
export type LoadTimelineStageState = "done" | "active" | "pending" | "failed";

export interface LoadTimelineStage {
  id: LoadTimelineStageId;
  state: LoadTimelineStageState;
  /** When the stage finished, from the top-up's own stamps; null while it has not. */
  at: string | null;
}

/**
 * Where a load from pesos is, as four stages. Built from the top-up's status and its own timestamps only, so
 * the line never claims the card has the money before the card says so.
 */
export function copLoadTimeline(
  topUp: Pick<CardCopTopUp, "status" | "created_at" | "delivered_at" | "credited_at" | "failed_at">,
): LoadTimelineStage[] {
  const status = topUp.status;
  if (status === "FAILED") {
    return [
      { id: "debited", state: "done", at: topUp.created_at },
      { id: "sending", state: "failed", at: topUp.failed_at },
      { id: "arrived", state: "pending", at: null },
      { id: "credited", state: "pending", at: null },
    ];
  }
  const delivered = status === "DELIVERED" || status === "CREDITED";
  const credited = status === "CREDITED";
  return [
    { id: "debited", state: "done", at: topUp.created_at },
    { id: "sending", state: delivered ? "done" : "active", at: topUp.delivered_at },
    { id: "arrived", state: delivered ? "done" : "pending", at: topUp.delivered_at },
    { id: "credited", state: credited ? "done" : delivered ? "active" : "pending", at: topUp.credited_at },
  ];
}

/**
 * The wait still ahead of a live top-up, for the ETA bar: counted from the placement while the USDT travels,
 * and from the arrival once only the card credit is left. Null once nothing is left to wait for.
 */
export function copLoadWait(
  topUp: Pick<CardCopTopUp, "status" | "created_at" | "delivered_at">,
  timing: CardCopTopUpTiming | null,
): { since: string; estimatedSeconds: number | null } | null {
  if (topUp.status === "PROCESSING") {
    return { since: topUp.created_at, estimatedSeconds: timing?.estimated_seconds ?? null };
  }
  if (topUp.status === "DELIVERED") {
    return {
      since: topUp.delivered_at ?? topUp.created_at,
      estimatedSeconds: timing?.credit_seconds ?? null,
    };
  }
  return null;
}

/** How the deposit-address read ended, for a backend that predates `funding-options`. */
export type LegacyCryptoAnswer =
  | { kind: "addresses"; count: number }
  | { kind: "failure"; failure: "not_enabled" | "not_active" | "error" };

export interface LegacyFundingInputs {
  cardId: string;
  /** The COP terms; null when they could not be read, and pesos is then not offered at all. */
  copTerms: { available: boolean; unavailable_reason: string | null } | null;
  crypto: LegacyCryptoAnswer;
  /** The card wallet balance as a decimal string; null when it could not be read. */
  walletBalance: string | null;
}

/**
 * The answer `funding-options` would give, rebuilt from the reads a backend without that route still serves.
 * Such a backend predates the opt-in, so every organization has the card wallet and may withdraw to it: the
 * dashboard then behaves as it did before, whatever order the two are deployed in.
 */
export function legacyFundingOptions({
  cardId,
  copTerms,
  crypto,
  walletBalance,
}: LegacyFundingInputs): CardFundingOptions {
  const cryptoReason =
    crypto.kind === "addresses"
      ? crypto.count > 0
        ? null
        : "card_not_eligible"
      : crypto.failure === "not_enabled"
        ? "card_deposits_not_enabled"
        : crypto.failure === "not_active"
          ? "card_not_active"
          : "unavailable";
  return {
    card_id: cardId,
    sources: [
      {
        kind: "COP",
        available: copTerms?.available === true,
        unavailable_reason: copTerms
          ? copTerms.available
            ? null
            : copTerms.unavailable_reason
          : COP_NOT_ROLLED_OUT,
        estimated_seconds: null,
      },
      {
        kind: "CRYPTO",
        available: cryptoReason === null,
        unavailable_reason: cryptoReason,
        estimated_seconds: null,
      },
      { kind: "CARD_WALLET", available: true, unavailable_reason: null, estimated_seconds: 0 },
    ],
    card_wallet_enabled: true,
    card_wallet_balance: walletBalance,
    withdrawals_allowed: true,
    cop_timing: null,
  };
}
